import { SubscriberRelationship } from '@bariendo/lib/insurance-coverage';
import { RelatedPerson } from '@medplum/fhirtypes';
import type { SubscriberInput } from '../../../../../../bots/src/handlers/update-insurance-coverage';

/**
 * The policyholder's four fields as a form holds them, and the `update-insurance-coverage` input
 * they make. Kept apart from the Insurance section so the PN card can write the same subscriber.
 */
export interface SubscriberFormValues {
  /** Unset when the Coverage has no relationship yet: the editor makes the user choose. */
  relationship?: SubscriberRelationship;
  firstName: string;
  lastName: string;
  /** `YYYY-MM-DD`, as a date input gives it. */
  birthDate: string;
}

/**
 * Who holds the plan, as the editor offers it. `Coverage.relationship` is the patient's
 * relationship to the subscriber, so the code for a parent's plan is `child`.
 */
export const SUBSCRIBER_RELATIONSHIP_OPTIONS: { value: SubscriberRelationship; label: string }[] = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Parent' },
  { value: 'other', label: 'Other' },
];

/**
 * Whether the subscriber may be written. Never blind: a policyholder that
 * didn't load would have to be retyped, unseen, over the one on file, and a
 * role whose policy can't read policyholders (Finance) would write one it could
 * never see again. Shared by the Insurance section and the PN card (BAR-2998).
 */
export function canWriteSubscriber(
  editable: boolean,
  canReadPolicyholders: boolean,
  relationship: SubscriberRelationship | undefined,
  policyholder: RelatedPerson | undefined
): boolean {
  return editable && canReadPolicyholders && (!relationship || relationship === 'self' || !!policyholder);
}

/** The form's starting values: the Coverage's relationship and the policyholder on file. */
export function subscriberFormValues(
  relationship: SubscriberRelationship | undefined,
  policyholder: RelatedPerson | undefined
): SubscriberFormValues {
  const name = policyholder?.name?.[0];
  return {
    relationship,
    firstName: name?.given?.join(' ') ?? '',
    lastName: name?.family ?? '',
    birthDate: policyholder?.birthDate ?? '',
  };
}

/**
 * The bot input for the form, or undefined while it would be refused: no relationship, or someone
 * else's plan without both names and a real, past birth date (the bot's own rules).
 */
export function subscriberInput(values: SubscriberFormValues, today = new Date()): SubscriberInput | undefined {
  const { relationship } = values;
  if (!relationship) return undefined;
  if (relationship === 'self') return { relationship };
  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  if (!firstName || !lastName || !isPastBirthDate(values.birthDate, today)) return undefined;
  return { relationship, firstName, lastName, birthDate: values.birthDate };
}

/**
 * A real calendar date (`2024-02-31` is refused), not after today: the bot's rule
 * (`update-insurance-coverage`'s `isPastDate`), kept in step by hand.
 */
export function isPastBirthDate(value: string, today = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value) && date.getTime() <= today.getTime();
}
